import React from 'react';
import { Link, LinkProps, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import { getUrlPrefix } from '../utils/languageUtils';

interface LocalizedLinkProps extends Omit<LinkProps, 'to'> {
  to: string;
}

const LocalizedLink: React.FC<LocalizedLinkProps> = ({ to, children, onClick, ...props }) => {
  const { language } = useI18n();
  const location = useLocation();
  const langPrefix = '/' + getUrlPrefix(language);
  
  // Handle anchor links differently
  if (to.startsWith('/#')) {
    const anchor = to.substring(2); // Get the anchor without '/#'
    const isOnHomePage = location.pathname === langPrefix || location.pathname === langPrefix + '/';
    
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isOnHomePage) {
        // If we're already on the home page, just scroll to the element
        e.preventDefault();
        const element = document.getElementById(anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
      // If custom onClick is provided, call it
      if (onClick) {
        onClick(e);
      }
    };
    
    // If on home page, use just the anchor, otherwise navigate to home page with anchor
    const href = isOnHomePage ? `#${anchor}` : `${langPrefix}/#${anchor}`;
    
    return (
      <Link to={href} onClick={handleClick} {...props}>
        {children}
      </Link>
    );
  }
  
  // If the path already has a language prefix, don't add another one
  const localizedPath = to.match(/^\/(en|ee|ru|fi)/) ? to : `${langPrefix}${to}`;
  
  return (
    <Link to={localizedPath} onClick={onClick} {...props}>
      {children}
    </Link>
  );
};

export default LocalizedLink;